import { Platform } from 'react-native';
import {
  generatePasskeyChallenge,
  registerPasskey,
  generatePasskeyAuthChallenge,
  authenticatePasskey,
  isWebAuthnSupported,
} from '@api/passkeyApi';
import {
  arrayBufferToBase64Url,
  base64UrlToArrayBuffer,
} from '@utils/webAuthnEncoding';

export const isConditionalPasskeyAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported() || typeof window === 'undefined') {
    return false;
  }
  try {
    const cred = window.PublicKeyCredential as typeof PublicKeyCredential & {
      isConditionalMediationAvailable?: () => Promise<boolean>;
    };
    return !!(await cred.isConditionalMediationAvailable?.());
  } catch {
    return false;
  }
};

const credentialToServerFormat = (
  credential: PublicKeyCredential,
  mode: 'create' | 'get'
) => {
  if (mode === 'create') {
    const response = credential.response as AuthenticatorAttestationResponse;
    return {
      id: credential.id,
      rawId: arrayBufferToBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
        attestationObject: arrayBufferToBase64Url(response.attestationObject),
      },
    };
  }

  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      signature: arrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle
        ? arrayBufferToBase64Url(response.userHandle)
        : null,
    },
  };
};

/**
 * Register a new passkey for the current user (must be signed in).
 */
export const registerNewPasskey = async (deviceName?: string): Promise<void> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported on this platform');
  }

  try {
    const challengeData = await generatePasskeyChallenge();

    if (!challengeData.user?.id) {
      throw new Error('You must be signed in to register a passkey');
    }

    const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
      challenge: base64UrlToArrayBuffer(challengeData.challenge),
      rp: challengeData.rp,
      user: {
        id: base64UrlToArrayBuffer(challengeData.user.id),
        name: challengeData.user.name,
        displayName: challengeData.user.displayName,
      },
      pubKeyCredParams: challengeData.pubKeyCredParams,
      timeout: challengeData.timeout,
      attestation: challengeData.attestation as AttestationConveyancePreference,
      authenticatorSelection: challengeData.authenticatorSelection,
      excludeCredentials: (challengeData.excludeCredentials || []).map(
        (cred: { id: string; type: PublicKeyCredentialType; transports?: AuthenticatorTransport[] }) => ({
          type: cred.type,
          id: base64UrlToArrayBuffer(cred.id),
          transports: cred.transports,
        })
      ),
    };

    const credential = (await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions,
    })) as PublicKeyCredential | null;

    if (!credential) {
      throw new Error('Failed to create credential');
    }

    await registerPasskey(credentialToServerFormat(credential, 'create'), deviceName);
  } catch (error: any) {
    if (error.name === 'NotAllowedError') {
      throw new Error('Passkey registration was cancelled or not allowed');
    }
    if (error.name === 'NotSupportedError') {
      throw new Error('Passkeys are not supported on this device');
    }
    if (error.name === 'InvalidStateError') {
      throw new Error('A passkey already exists for this account');
    }
    throw error;
  }
};

/**
 * Authenticate with passkey for a known email address.
 */
export const authenticateWithPasskey = async (email: string): Promise<any> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported on this platform');
  }

  if (!email?.trim()) {
    throw new Error('Email is required for passkey sign-in');
  }

  try {
    const challengeData = await generatePasskeyAuthChallenge(email.trim());

    const allowCredentials = (challengeData.allowCredentials || []).map(
      (cred: { id: string; type: string; transports?: string[] }) => ({
        type: cred.type,
        id: base64UrlToArrayBuffer(cred.id),
        transports: cred.transports,
      })
    );

    const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
      challenge: base64UrlToArrayBuffer(challengeData.challenge),
      rpId: challengeData.rpId,
      allowCredentials,
      timeout: challengeData.timeout,
      userVerification: challengeData.userVerification as UserVerificationRequirement,
    };

    const credential = (await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions,
    })) as PublicKeyCredential | null;

    if (!credential) {
      throw new Error('Failed to get credential');
    }

    return await authenticatePasskey(credentialToServerFormat(credential, 'get'));
  } catch (error: any) {
    if (error.name === 'NotAllowedError') {
      throw new Error('Passkey authentication was cancelled or not allowed');
    }
    if (error.name === 'NotSupportedError') {
      throw new Error('Passkeys are not supported on this device');
    }
    if (error.name === 'InvalidStateError') {
      throw new Error('No passkey found for this account');
    }
    throw error;
  }
};

/**
 * Browser autofill passkey (conditional mediation) — no email required.
 * Call once on login page mount when supported.
 */
export const authenticateWithConditionalPasskey = async (): Promise<any | null> => {
  if (!isWebAuthnSupported() || Platform.OS !== 'web') {
    return null;
  }

  const available = await isConditionalPasskeyAvailable();
  if (!available) {
    return null;
  }

  try {
    const challengeData = await generatePasskeyAuthChallenge();

    const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
      challenge: base64UrlToArrayBuffer(challengeData.challenge),
      rpId: challengeData.rpId,
      timeout: challengeData.timeout,
      userVerification: 'preferred',
    };

    const credential = (await navigator.credentials.get({
      mediation: 'conditional',
      publicKey: publicKeyCredentialRequestOptions,
    })) as PublicKeyCredential | null;

    if (!credential) {
      return null;
    }

    return await authenticatePasskey(credentialToServerFormat(credential, 'get'));
  } catch (error: any) {
    if (error?.name === 'NotAllowedError') {
      return null;
    }
    throw error;
  }
};
